/**
 * DB-backed reference-data repository for the marketplace `metadata_*`
 * dimensions (Bundesländer, PLZ, Naturraum, Kompensationsraum).
 *
 * The Supabase client is INJECTED so each caller binds the right one:
 *   - service-role where the call runs inside a `'use cache'` scope (ARCH-027
 *     forbids `cookies()` there) or needs to bypass RLS for a cross-user read,
 *   - the cookie/request client elsewhere.
 * The client is imported TYPE-ONLY (`import type`), so this package keeps ZERO
 * runtime dependencies and stays safe to value-import from client components
 * (the type is erased at build time).
 *
 * Methods return RAW rows; caching (e.g. Vercel Runtime Cache) and
 * response-shaping (the funnel's `PLZLookupResult`, landvest's index
 * enrichment) stay in each app's service/route layer. ONE deliberate
 * exception: the `plzResolve*` reads compose the canonical
 * Postfach/Großempfänger nearest-delivery-PLZ fallback over the raw reads
 * (`{ rows, fallback }` envelopes), so every consumer substitutes
 * identically; the raw `plzGetRow`/`plzListRows`/`plzListByCodes` reads
 * remain for exact-only callers.
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import type { Bundesland } from "./bundesland";
import type {
  InstrumentRow,
  KompensationsraumRow,
  KompensationsraumZipMatchRef,
  MetadataNaturraumRow,
  NaturraumOption,
  NaturraumZipMatch,
  NaturraumZipMatchRef,
  PlzRow,
  PlzSearchArgs,
  PlzUpsertInput,
} from "./types";

export interface BundeslandRow {
  code: string;
  name: Bundesland;
}

const PLZ_COLS = "plz, bundesland, kreis, kreis_type, lat, lon";

/**
 * Default max numeric distance for nearest-delivery-PLZ substitution.
 *
 * Non-delivery PLZs (Postfach / Großempfänger / Aktions-PLZ) have no
 * metadata_plz row but are allocated numerically adjacent to their town's
 * delivery block. The nine Postfach PLZs real users typed on 2026-07-06..08
 * (94003 → Passau 94032, 12243 → Berlin 12247, …) all resolve at distances
 * 1–29; 50 adds headroom while keeping a mistyped PLZ in a sparse region
 * from resolving to a far-away town.
 */
export const NEAREST_PLZ_MAX_DISTANCE = 50;

/**
 * A write Postgres rejected, carrying the SQLSTATE so callers can tell a
 * conflict from a failure.
 *
 * Every other method here throws a plain `Error` — losing the code is harmless
 * when the only sane response is "it broke". It is NOT harmless for
 * {@link MetadataRepo.naturraumZipMatchInsert}: `23505` (unique_violation) means
 * the mapping already exists, which its admin caller answers with 409 rather
 * than 500. Widen this to other writes only when a caller needs the same
 * distinction.
 */
export class MetadataWriteError extends Error {
  /** Postgres SQLSTATE, e.g. `"23505"` for a unique violation. */
  readonly code: string | undefined;

  constructor(message: string, code: string | undefined) {
    super(message);
    this.name = "MetadataWriteError";
    this.code = code;
  }
}

/** The nearest existing PLZ on one side of a target, as a substitution candidate. */
export interface NearestPlz {
  plz: string;
  distance: number;
}

/** A PLZ row-set resolution: exact rows (`fallback: null`) or the nearest
 *  delivery PLZ's rows plus the substitution marker. */
export interface PlzResolution {
  rows: PlzRow[];
  fallback: NearestPlz | null;
}

/** Border-safe single-row variant of {@link PlzResolution}. */
export interface PlzRowResolution {
  row: PlzRow;
  fallback: NearestPlz | null;
}

/** Batch resolution entry, keyed by the INPUT code (not the substitute). */
export interface PlzCodeResolution extends PlzResolution {
  plz: string;
}

/** Map a `{ zone_code, name }[]` RPC payload onto NaturraumOption with 1-based rank. */
function withRank(rows: Array<{ zone_code: string; name: string }> | null): NaturraumOption[] {
  return (rows ?? []).map((row, i) => ({
    zone_code: row.zone_code,
    name: row.name,
    rank: i + 1,
  }));
}

export interface MetadataRepo {
  // ── metadata_bundeslaender ──────────────────────────────────────────────
  /** All 16 canonical Bundesländer `{ code, name }`, as stored in the DB. */
  listBundeslaender(): Promise<BundeslandRow[]>;
  /** Just the canonical names. */
  listBundeslaenderNames(): Promise<string[]>;

  // ── metadata_plz ────────────────────────────────────────────────────────
  /** Prefix/Bundesland search (admin PLZ tools). */
  plzSearch(args: PlzSearchArgs): Promise<PlzRow[]>;
  /** A single representative row for a PLZ — border-safe (order+limit1). */
  plzGetRow(plz: string): Promise<PlzRow | null>;
  /** Every row for a PLZ (>1 only for the ~18 border PLZs; funnel disambiguates). */
  plzListRows(plz: string): Promise<PlzRow[]>;
  /** Rows for a set of PLZ codes (batch coordinate / import lookups). */
  plzListByCodes(plzCodes: string[]): Promise<PlzRow[]>;
  /** The exact row for a `(plz, bundesland)` composite-key pair. */
  plzGetByPlzAndBundesland(plz: string, bundesland: string): Promise<PlzRow | null>;
  /**
   * Nearest existing PLZ codes strictly below and above a (missing) PLZ —
   * each `null` at the respective end of the table. Zero-padded 5-digit codes
   * sort lexicographically = numerically, so both probes are index scans.
   * Low-level probe behind {@link MetadataRepo.plzNearestWithinDistance}.
   */
  plzNearestCodes(plz: string): Promise<{ lower: string | null; upper: string | null }>;
  /**
   * The nearest existing PLZ within `maxDistance` of a (missing) PLZ, or null.
   * Ties prefer the UPPER neighbour: a town's delivery block sits numerically
   * above its Postfach/Großempfänger block (e.g. Passau: Postfächer 94001–17,
   * delivery 94032–36), so upper is the likelier same-town match.
   * Backs every nearest-delivery-PLZ fallback (region lookup, coordinates).
   */
  plzNearestWithinDistance(plz: string, maxDistance?: number): Promise<NearestPlz | null>;
  /**
   * THE canonical PLZ read: exact rows if the PLZ exists, else the nearest
   * delivery PLZ's rows (Postfach/Großempfänger fallback, threshold
   * {@link NEAREST_PLZ_MAX_DISTANCE}) with a marker, else null. Rows are
   * ordered by bundesland (border-safe). Consumers that derive
   * naturraum/zip-match data MUST use `rows[0].plz` (the effective PLZ),
   * not their input.
   */
  plzResolveRows(plz: string): Promise<PlzResolution | null>;
  /** Single-row convenience over {@link MetadataRepo.plzResolveRows}. */
  plzResolveRow(plz: string): Promise<PlzRowResolution | null>;
  /**
   * Batch variant of {@link MetadataRepo.plzResolveRows}: one entry per
   * DISTINCT input code that resolved (exactly or via fallback); unresolvable
   * codes are absent. Entries are keyed by the INPUT code.
   */
  plzResolveByCodes(plzCodes: string[]): Promise<PlzCodeResolution[]>;
  /** Insert/update a PLZ row on the `(plz, bundesland)` PK (admin; service-role). */
  plzUpsert(input: PlzUpsertInput): Promise<void>;

  // ── metadata_naturraum_zip_match ────────────────────────────────────────
  /** BfN-Naturraum zip-matches for a PLZ, ordered by rank. */
  naturraumMatchesByPlz(plz: string): Promise<NaturraumZipMatchRef[]>;
  /** Batch variant: zip-matches (incl. `plz`) for a set of PLZ codes (import). */
  naturraumMatchesByPlzCodes(plzCodes: string[]): Promise<NaturraumZipMatch[]>;
  /**
   * Insert a single zip-match row (admin). Throws {@link MetadataWriteError};
   * `code === "23505"` means this `(plz, naturraum)` mapping already exists.
   *
   * `(plz, naturraum)` and NOT `(plz, zone)`: `idx_metadata_naturraum_zip_match_unique`
   * is on the NAME, and mig `20260824123000` says so explicitly ("no unique
   * constraint on (plz, zone)"). The two coincide only while `metadata_naturraum`
   * happens to map zone↔naturraum 1:1 — nothing enforces that, since its `zone`
   * and `naturraum` indexes are both non-unique.
   */
  naturraumZipMatchInsert(row: {
    plz: string;
    zone: string;
    naturraum: string;
    rank: number;
  }): Promise<void>;
  /** Delete the zip-match rows for a `(plz, zone)` pair (admin). */
  naturraumZipMatchDelete(plz: string, zone: string): Promise<void>;

  // ── metadata_naturraum (PLZ-pipeline reference; NOT the `naturraum` FK table) ──
  /** All rows, ordered by rank. */
  naturraumMetaList(): Promise<MetadataNaturraumRow[]>;
  /**
   * `{ zone, naturraum }` for every row, ordered by zone — the vocabulary the
   * admin PLZ↔Naturraum editor offers. A projection rather than a reuse of
   * {@link MetadataRepo.naturraumMetaList} because the picker wants zone order,
   * and ordering a few hundred rows in the database beats re-sorting them here.
   */
  naturraumMetaZones(): Promise<Array<{ zone: string; naturraum: string }>>;
  /** Rows for a set of Naturraum names. */
  naturraumMetaByNames(names: string[]): Promise<MetadataNaturraumRow[]>;
  /** Resolve a Naturraum NAME → its BfN zone code (`zone`). */
  naturraumZoneByName(naturraum: string): Promise<string | null>;
  /** Resolve a BfN zone code → its Naturraum NAME. */
  naturraumNameByZone(zone: string): Promise<string | null>;
  /** `{ zone, state_aliases }` for every row (backs the website zone-alias cache). */
  naturraumStateAliasRows(): Promise<
    Array<{ zone: string; state_aliases: Record<string, string[]> | null }>
  >;

  // ── metadata_kompensationsraum_zip_match ────────────────────────────────
  /** State-binding Kompensationsraum options for a PLZ (embed flattened). */
  kompensationsraumMatchesByPlz(plz: string): Promise<KompensationsraumZipMatchRef[]>;

  // ── kompensationsraum (state-published rows only) ───────────────────────
  /**
   * Every state-published Kompensationsraum (LANUV K-zones, Brandenburg LaPro,
   * the Berlin sentinel), ordered by zone code then name — the register a typed
   * K-code resolves against and a stored `kompensationsraum_id` is checked
   * against. About twenty rows; callers cache them.
   */
  kompensationsraumAuthoritativeRows(): Promise<KompensationsraumRow[]>;

  // ── metadata_compensation_instruments / metadata_instrument_coverage ────
  /** All instruments, ordered by level → bundesland → short_name. */
  instrumentsList(): Promise<InstrumentRow[]>;
  /**
   * Instruments applicable to a Bundesland, ordered by name: the state's own
   * plus the federal ones (`bundesland IS NULL`). `null` returns every
   * instrument in the same order — the "Bundesland not known yet" case, which
   * OCR instrument detection hits before the document has been parsed.
   *
   * The filter is DB-side on purpose: fetching all and filtering in memory is
   * the pattern this method exists to replace (CLAUDE.md §3b.5).
   *
   * `bundesland` is interpolated into a PostgREST `or=` filter, so it must be a
   * plain state name. ENFORCED here: a value carrying anything but letters,
   * spaces and hyphens is rejected rather than interpolated (see the
   * implementation) — the caller is still expected to pass a real state name,
   * but a caller that forgets gets an error, not a rewritten filter.
   */
  instrumentsListByBundesland(bundesland: string | null): Promise<InstrumentRow[]>;
  /** A single instrument by id (null if not found). */
  instrumentGetById(id: string): Promise<InstrumentRow | null>;
  /** Insert an instrument; returns the created row (admin). */
  instrumentCreate(input: Record<string, unknown>): Promise<InstrumentRow>;
  /** Update an instrument by id; returns the updated row (admin). */
  instrumentUpdate(id: string, input: Record<string, unknown>): Promise<InstrumentRow>;
  /** Delete an instrument by id (admin). */
  instrumentDelete(id: string): Promise<void>;
  /** Coverage rows for an instrument, ordered by level → bundesland. */
  instrumentCoverageList(instrumentId: string): Promise<InstrumentRow[]>;
  /** Insert a coverage row for an instrument; returns it (admin). */
  instrumentCoverageCreate(
    instrumentId: string,
    input: Record<string, unknown>
  ): Promise<InstrumentRow>;
  /** Delete a coverage row by id (admin). */
  instrumentCoverageDelete(coverageId: string): Promise<void>;

  // ── RPCs (main DB) ──────────────────────────────────────────────────────
  /** `get_all_naturraum` → all BfN-Naturräume `{ zone_code, name, rank }`. */
  rpcAllNaturraum(): Promise<NaturraumOption[]>;
  /**
   * `get_naturraum_by_bundesland` — DELIBERATELY named for what it RETURNS:
   * Kompensationsräume / K-zones (despite the DB function's misleading name).
   * For BfN-Naturräume use {@link rpcBfnNaturraumByBundesland}.
   */
  rpcKompensationsraeumeByBundesland(bundesland: string): Promise<NaturraumOption[]>;
  /** `get_bfn_naturraum_by_bundesland` → BfN-Naturräume (order-book vocabulary). */
  rpcBfnNaturraumByBundesland(bundesland: string): Promise<NaturraumOption[]>;
  /**
   * `get_instruments_for_plz` → the instruments a PLZ + Bundesland actually
   * admits. This is the PRECISE list (it consults
   * `metadata_instrument_coverage`); {@link MetadataRepo.instrumentsListByBundesland}
   * is the coarse Bundesland-only fallback for when no PLZ is known.
   */
  rpcInstrumentsForPlz(plz: string, bundesland: string): Promise<InstrumentRow[]>;
}

export function createReferenceRepo(client: SupabaseClient): MetadataRepo {
  async function listBundeslaender(): Promise<BundeslandRow[]> {
    const { data, error } = await client.from("metadata_bundeslaender").select("code, name");
    if (error) throw new Error(`Failed to load Bundesländer: ${error.message}`);
    return (data ?? []).map((row) => ({
      code: String(row.code),
      name: String(row.name) as Bundesland,
    }));
  }

  async function plzNearestCodes(
    plz: string
  ): Promise<{ lower: string | null; upper: string | null }> {
    const [lower, upper] = await Promise.all([
      client
        .from("metadata_plz")
        .select("plz")
        .lt("plz", plz)
        .order("plz", { ascending: false })
        .limit(1)
        .maybeSingle(),
      client
        .from("metadata_plz")
        .select("plz")
        .gt("plz", plz)
        .order("plz", { ascending: true })
        .limit(1)
        .maybeSingle(),
    ]);
    const failed = lower.error ?? upper.error;
    if (failed) throw new Error(`Failed to fetch nearest PLZ: ${failed.message}`);
    return {
      lower: (lower.data as { plz: string } | null)?.plz ?? null,
      upper: (upper.data as { plz: string } | null)?.plz ?? null,
    };
  }

  async function nearestWithinDistance(
    plz: string,
    maxDistance: number = NEAREST_PLZ_MAX_DISTANCE
  ): Promise<NearestPlz | null> {
    const { lower, upper } = await plzNearestCodes(plz);
    const target = Number(plz);
    // Each side is gated by the threshold independently; on a distance tie
    // the upper neighbour wins (delivery blocks sit above Postfach blocks).
    const upperNear =
      upper !== null && Number(upper) - target <= maxDistance
        ? { plz: upper, distance: Number(upper) - target }
        : null;
    const lowerNear =
      lower !== null && target - Number(lower) <= maxDistance
        ? { plz: lower, distance: target - Number(lower) }
        : null;
    if (upperNear && lowerNear) {
      return upperNear.distance <= lowerNear.distance ? upperNear : lowerNear;
    }
    return upperNear ?? lowerNear;
  }

  async function listRowsByPlz(plz: string): Promise<PlzRow[]> {
    const { data, error } = await client
      .from("metadata_plz")
      .select(PLZ_COLS)
      .eq("plz", plz)
      .order("bundesland", { ascending: true });
    if (error) throw new Error(`Failed to fetch PLZ rows: ${error.message}`);
    return (data ?? []) as PlzRow[];
  }

  async function listRowsByCodes(plzCodes: string[]): Promise<PlzRow[]> {
    if (plzCodes.length === 0) return [];
    // Ordered in the DB so a border PLZ's representative row (rows[0] after
    // grouping) uses the SAME collation as listRowsByPlz — a JS sort could
    // disagree with Postgres on umlauts/NULLs.
    const { data, error } = await client
      .from("metadata_plz")
      .select(PLZ_COLS)
      .in("plz", plzCodes)
      .order("plz", { ascending: true })
      .order("bundesland", { ascending: true });
    if (error) throw new Error(`Failed to fetch PLZ rows: ${error.message}`);
    return (data ?? []) as PlzRow[];
  }

  /** The shared substitution step: nearest delivery PLZ within the threshold,
   *  or null. Empty substitute rows = the neighbour vanished between the
   *  probe and the read — treated as unresolvable. */
  async function substituteFromNearest(plz: string): Promise<PlzResolution | null> {
    const nearest = await nearestWithinDistance(plz);
    if (!nearest) return null;
    const rows = await listRowsByPlz(nearest.plz);
    if (rows.length === 0) return null;
    return { rows, fallback: nearest };
  }

  async function resolveRows(plz: string): Promise<PlzResolution | null> {
    const rows = await listRowsByPlz(plz);
    if (rows.length > 0) return { rows, fallback: null };
    return substituteFromNearest(plz);
  }

  // Bounds the per-missing-code probe fan-out in resolveByCodes: a dirty bulk
  // import can carry thousands of unknown codes, and each probe costs up to 3
  // PostgREST requests — unbounded Promise.all would flood the DB pool.
  const SUBSTITUTE_CONCURRENCY = 8;

  async function resolveByCodes(plzCodes: string[]): Promise<PlzCodeResolution[]> {
    const unique = [...new Set(plzCodes)];
    if (unique.length === 0) return [];
    const exactRows = await listRowsByCodes(unique);
    const grouped = new Map<string, PlzRow[]>();
    for (const row of exactRows) {
      const list = grouped.get(row.plz) ?? [];
      list.push(row);
      grouped.set(row.plz, list);
    }
    const resolved: PlzCodeResolution[] = unique
      .filter((code) => grouped.has(code))
      .map((code) => ({ plz: code, rows: grouped.get(code)!, fallback: null }));
    const missing = unique.filter((code) => !grouped.has(code));
    for (let i = 0; i < missing.length; i += SUBSTITUTE_CONCURRENCY) {
      const chunk = missing.slice(i, i + SUBSTITUTE_CONCURRENCY);
      const substituted = await Promise.all(
        chunk.map(async (code) => {
          const substitution = await substituteFromNearest(code);
          return substitution ? { plz: code, ...substitution } : null;
        })
      );
      resolved.push(...substituted.filter((entry): entry is PlzCodeResolution => entry !== null));
    }
    return resolved;
  }

  return {
    // ── bundeslaender ──
    listBundeslaender,
    async listBundeslaenderNames() {
      return (await listBundeslaender()).map((r) => r.name);
    },

    // ── plz ──
    async plzSearch(args) {
      let query = client.from("metadata_plz").select(PLZ_COLS);
      if (args.prefix) query = query.ilike("plz", `${args.prefix}%`);
      if (args.bundesland) query = query.eq("bundesland", args.bundesland);
      const { data, error } = await query.order("plz", { ascending: true }).limit(args.limit ?? 50);
      if (error) throw new Error(`Failed to search PLZ: ${error.message}`);
      return (data ?? []) as PlzRow[];
    },
    async plzGetRow(plz) {
      const { data, error } = await client
        .from("metadata_plz")
        .select(PLZ_COLS)
        .eq("plz", plz)
        .order("bundesland", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(`Failed to fetch PLZ: ${error.message}`);
      return (data ?? null) as PlzRow | null;
    },
    plzListRows: listRowsByPlz,
    plzListByCodes: listRowsByCodes,
    async plzGetByPlzAndBundesland(plz, bundesland) {
      const { data, error } = await client
        .from("metadata_plz")
        .select(PLZ_COLS)
        .eq("plz", plz)
        .eq("bundesland", bundesland)
        .maybeSingle();
      if (error) throw new Error(`Failed to fetch PLZ: ${error.message}`);
      return (data ?? null) as PlzRow | null;
    },
    async plzUpsert(input) {
      const { error } = await client
        .from("metadata_plz")
        .upsert(input, { onConflict: "plz,bundesland" });
      if (error) throw new Error(`Failed to upsert PLZ: ${error.message}`);
    },
    plzNearestCodes,
    plzNearestWithinDistance: nearestWithinDistance,
    plzResolveRows: resolveRows,
    async plzResolveRow(plz) {
      const resolved = await resolveRows(plz);
      // rows[0] is border-safe: listRowsByPlz orders by bundesland.
      return resolved ? { row: resolved.rows[0], fallback: resolved.fallback } : null;
    },
    plzResolveByCodes: resolveByCodes,

    // ── naturraum zip-match ──
    async naturraumMatchesByPlz(plz) {
      const { data, error } = await client
        .from("metadata_naturraum_zip_match")
        .select("naturraum, zone, rank")
        .eq("plz", plz)
        .order("rank", { ascending: true });
      if (error) throw new Error(`Failed to fetch Naturraum matches: ${error.message}`);
      return (data ?? []) as NaturraumZipMatchRef[];
    },
    async naturraumMatchesByPlzCodes(plzCodes) {
      if (plzCodes.length === 0) return [];
      const { data, error } = await client
        .from("metadata_naturraum_zip_match")
        .select("plz, naturraum, zone, rank")
        .in("plz", plzCodes)
        .order("rank", { ascending: true });
      if (error) throw new Error(`Failed to fetch Naturraum matches: ${error.message}`);
      return (data ?? []) as NaturraumZipMatch[];
    },
    async naturraumZipMatchInsert(row) {
      const { error } = await client.from("metadata_naturraum_zip_match").insert(row);
      if (error)
        throw new MetadataWriteError(
          `Failed to insert Naturraum zip-match: ${error.message}`,
          (error as { code?: string }).code
        );
    },
    async naturraumZipMatchDelete(plz, zone) {
      const { error } = await client
        .from("metadata_naturraum_zip_match")
        .delete()
        .eq("plz", plz)
        .eq("zone", zone);
      if (error) throw new Error(`Failed to delete Naturraum zip-match: ${error.message}`);
    },

    // ── metadata_naturraum ──
    async naturraumMetaList() {
      const { data, error } = await client
        .from("metadata_naturraum")
        .select("*")
        .order("rank", { ascending: true });
      if (error) throw new Error(`Failed to fetch metadata_naturraum: ${error.message}`);
      return (data ?? []) as MetadataNaturraumRow[];
    },
    async naturraumMetaZones() {
      const { data, error } = await client
        .from("metadata_naturraum")
        .select("zone, naturraum")
        .order("zone", { ascending: true });
      if (error) throw new Error(`Failed to fetch metadata_naturraum: ${error.message}`);
      return (data ?? []) as Array<{ zone: string; naturraum: string }>;
    },
    async naturraumMetaByNames(names) {
      if (names.length === 0) return [];
      const { data, error } = await client
        .from("metadata_naturraum")
        .select("*")
        .in("naturraum", names);
      if (error) throw new Error(`Failed to fetch metadata_naturraum: ${error.message}`);
      return (data ?? []) as MetadataNaturraumRow[];
    },
    async naturraumZoneByName(naturraum) {
      const { data, error } = await client
        .from("metadata_naturraum")
        .select("zone")
        .eq("naturraum", naturraum)
        .maybeSingle();
      if (error) throw new Error(`Failed to resolve Naturraum zone: ${error.message}`);
      return (data?.zone as string | undefined) ?? null;
    },
    async naturraumNameByZone(zone) {
      const { data, error } = await client
        .from("metadata_naturraum")
        .select("naturraum")
        .eq("zone", zone)
        .maybeSingle();
      if (error) throw new Error(`Failed to resolve Naturraum name: ${error.message}`);
      return (data?.naturraum as string | undefined) ?? null;
    },
    async naturraumStateAliasRows() {
      const { data, error } = await client.from("metadata_naturraum").select("zone, state_aliases");
      if (error) throw new Error(`Failed to fetch Naturraum state aliases: ${error.message}`);
      return (data ?? []) as Array<{
        zone: string;
        state_aliases: Record<string, string[]> | null;
      }>;
    },

    // ── kompensationsraum zip-match ──
    async kompensationsraumMatchesByPlz(plz) {
      const { data, error } = await client
        .from("metadata_kompensationsraum_zip_match")
        .select(
          "rank, kompensationsraum:kompensationsraum_id(id, name, zone_code, state_code, bundesland_name, legal_source)"
        )
        .eq("plz", plz)
        .order("rank", { ascending: true });
      if (error) throw new Error(`Failed to fetch Kompensationsraum matches: ${error.message}`);
      type EmbedRow = {
        rank: number;
        kompensationsraum: {
          id: string;
          name: string;
          zone_code: string;
          state_code: string | null;
          bundesland_name: string;
          legal_source: string;
        } | null;
      };
      return ((data ?? []) as unknown as EmbedRow[])
        .filter(
          (
            row
          ): row is EmbedRow & {
            kompensationsraum: NonNullable<EmbedRow["kompensationsraum"]>;
          } => row.kompensationsraum != null
        )
        .map((row) => ({
          kompensationsraum_id: row.kompensationsraum.id,
          name: row.kompensationsraum.name,
          zone_code: row.kompensationsraum.zone_code,
          state_code: row.kompensationsraum.state_code,
          bundesland_name: row.kompensationsraum.bundesland_name,
          legal_source: row.kompensationsraum.legal_source,
          rank: row.rank,
        }));
    },
    async kompensationsraumAuthoritativeRows() {
      const { data, error } = await client
        .from("kompensationsraum")
        .select("id, name, zone_code, state_code, bundesland_name, legal_source")
        // `legal_source` is NOT NULL DEFAULT 'auto-derived' (mig 20260430162000),
        // so this keeps exactly the state-published rows.
        .neq("legal_source", "auto-derived")
        .order("zone_code")
        .order("name");
      if (error) throw new Error(`Failed to list Kompensationsräume: ${error.message}`);
      return (data ?? []) as KompensationsraumRow[];
    },

    // ── instruments / coverage ──
    async instrumentsList() {
      const { data, error } = await client
        .from("metadata_compensation_instruments")
        .select("*")
        .order("level", { ascending: true })
        .order("bundesland", { ascending: true })
        .order("short_name", { ascending: true });
      if (error) throw new Error(`Failed to list instruments: ${error.message}`);
      return (data ?? []) as InstrumentRow[];
    },
    async instrumentsListByBundesland(bundesland) {
      let query = client
        .from("metadata_compensation_instruments")
        .select("*")
        .order("name", { ascending: true });
      if (bundesland) {
        // `.or()` takes a FILTER EXPRESSION, not a bound parameter: a comma, a
        // parenthesis or a quote in this value REWRITES the expression rather
        // than being matched. The doc above says the caller must not pass
        // filter syntax — this is what makes that true rather than hopeful.
        // The only in-repo caller reads the value straight off a multipart
        // form, and this is a public method on a shared package, so the next
        // caller inherits whatever it does.
        //
        // A Bundesland name is letters, spaces and hyphens; rejecting is safer
        // than escaping a grammar we do not own.
        if (!/^[\p{L} \-]{1,64}$/u.test(bundesland)) {
          throw new Error(`Invalid Bundesland: ${JSON.stringify(bundesland)}`);
        }
        query = query.or(`bundesland.eq.${bundesland},bundesland.is.null`);
      }
      const { data, error } = await query;
      if (error) throw new Error(`Failed to list instruments: ${error.message}`);
      return (data ?? []) as InstrumentRow[];
    },
    async instrumentGetById(id) {
      const { data, error } = await client
        .from("metadata_compensation_instruments")
        .select("*")
        .eq("id", id)
        .single();
      if (error) {
        if (error.code === "PGRST116") return null;
        throw new Error(`Failed to fetch instrument: ${error.message}`);
      }
      return data as InstrumentRow;
    },
    async instrumentCreate(input) {
      const { data, error } = await client
        .from("metadata_compensation_instruments")
        .insert(input)
        .select()
        .single();
      if (error) throw new Error(`Failed to create instrument: ${error.message}`);
      return data as InstrumentRow;
    },
    async instrumentUpdate(id, input) {
      const { data, error } = await client
        .from("metadata_compensation_instruments")
        .update(input)
        .eq("id", id)
        .select()
        .single();
      if (error) throw new Error(`Failed to update instrument: ${error.message}`);
      return data as InstrumentRow;
    },
    async instrumentDelete(id) {
      const { error } = await client
        .from("metadata_compensation_instruments")
        .delete()
        .eq("id", id);
      if (error) throw new Error(`Failed to delete instrument: ${error.message}`);
    },
    async instrumentCoverageList(instrumentId) {
      const { data, error } = await client
        .from("metadata_instrument_coverage")
        .select("*")
        .eq("instrument_id", instrumentId)
        .order("level", { ascending: true })
        .order("bundesland", { ascending: true });
      if (error) throw new Error(`Failed to list coverage: ${error.message}`);
      return (data ?? []) as InstrumentRow[];
    },
    async instrumentCoverageCreate(instrumentId, input) {
      const { data, error } = await client
        .from("metadata_instrument_coverage")
        .insert({ instrument_id: instrumentId, ...input })
        .select()
        .single();
      if (error) throw new Error(`Failed to create coverage: ${error.message}`);
      return data as InstrumentRow;
    },
    async instrumentCoverageDelete(coverageId) {
      const { error } = await client
        .from("metadata_instrument_coverage")
        .delete()
        .eq("id", coverageId);
      if (error) throw new Error(`Failed to delete coverage: ${error.message}`);
    },

    // ── RPCs ──
    async rpcAllNaturraum() {
      const { data, error } = await client.rpc("get_all_naturraum");
      if (error) throw new Error(`Failed to get naturraum list: ${error.message}`);
      return withRank(data as { zone_code: string; name: string }[] | null);
    },
    async rpcKompensationsraeumeByBundesland(bundesland) {
      const { data, error } = await client.rpc("get_naturraum_by_bundesland", {
        p_bundesland: bundesland,
      });
      if (error)
        throw new Error(`Failed to get Kompensationsräume for ${bundesland}: ${error.message}`);
      return withRank(data as { zone_code: string; name: string }[] | null);
    },
    async rpcBfnNaturraumByBundesland(bundesland) {
      const { data, error } = await client.rpc("get_bfn_naturraum_by_bundesland", {
        p_bundesland: bundesland,
      });
      if (error) throw new Error(`Failed to get BfN naturraum for ${bundesland}: ${error.message}`);
      // The function returns `{ naturraum, zone }`; normalise onto NaturraumOption.
      return withRank(
        (data as { naturraum: string; zone: string }[] | null)?.map((r) => ({
          zone_code: r.zone,
          name: r.naturraum,
        })) ?? null
      );
    },
    async rpcInstrumentsForPlz(plz, bundesland) {
      const { data, error } = await client.rpc("get_instruments_for_plz", {
        p_plz: plz,
        p_bundesland: bundesland,
      });
      if (error)
        throw new Error(`Failed to get instruments for ${plz}/${bundesland}: ${error.message}`);
      return (data ?? []) as InstrumentRow[];
    },
  };
}
